// Money as real currency (wave 10, docs/build/CONTRACTS.md "Wave 10: real money"). Every amount is integer minor units
// plus an ISO currency, `{amount_minor, currency, formatted?}`. Launch currencies: USD and EUR.
//
// `formatMoney` is the contract's helper from money's sdk/purchase/src/price.ts (symbol first, two decimals, no
// locale: `$4.99`, `€4.99`), re-exported here; test/wave10.test.ts checks it against sdk/purchase/test/format-vectors.json.
//
// The viewer's currency is their wallet's (money's `GET /v1/me/wallet`), fixed by their first top-up. Signed out, no
// top-up yet, or money unreachable: USD.

import { api } from "./api.ts";
import { formatMoney, isCurrency, type Currency } from "../../../sdk/purchase/src/price.ts";

export { CURRENCIES, formatMoney, isCurrency, type Currency } from "../../../sdk/purchase/src/price.ts";

/** A money amount as the services send it (`formatted` only in responses). */
export interface Money {
  amount_minor: number;
  currency: Currency;
  formatted?: string;
}

/** A `Money` from a service, or null when it's malformed. */
export function parseMoney(raw: unknown): Money | null {
  if (!raw || typeof raw !== "object") return null;
  const r = raw as Record<string, unknown>;
  if (!Number.isSafeInteger(r.amount_minor) || !isCurrency(r.currency)) return null;
  return { amount_minor: r.amount_minor as number, currency: r.currency };
}

/** Shows a `Money` with our own formatter (a service's `formatted` isn't needed). */
export function showMoney(m: unknown, fallback = "—"): string {
  const p = parseMoney(m);
  return p ? formatMoney(p.amount_minor, p.currency) : fallback;
}

// ---- the viewer's wallet currency ---------------------------------------------------------------------------

let viewer: Currency | null = null;

/** The viewer's wallet currency, USD when it isn't known. */
export function viewerCurrency(): Currency {
  return viewer ?? "USD";
}

export function setViewerCurrency(c: Currency | null): void {
  viewer = c;
}

/** Money's wallet: `currency` is null before the first top-up. */
export function walletCurrency(raw: unknown): Currency | null {
  const c = raw && typeof raw === "object" ? (raw as { currency?: unknown }).currency : null;
  return isCurrency(c) ? c : null;
}

/**
 * Loads the signed-in viewer's wallet currency (`GET /v1/me/wallet`). Signed out or on any error: unknown (USD).
 * `fetchWallet` is for tests.
 */
export async function loadViewerCurrency(
  signedIn: boolean,
  fetchWallet: () => Promise<unknown> = () => api<unknown>("money", "/me/wallet"),
): Promise<Currency> {
  if (!signedIn) {
    viewer = null;
    return viewerCurrency();
  }
  try {
    viewer = walletCurrency(await fetchWallet());
  } catch {
    viewer = null;
  }
  return viewerCurrency();
}

// ---- prices ---------------------------------------------------------------------------------------------------

export type Prices = Partial<Record<Currency, unknown>>;

/**
 * An item's price in `currency`. Store items carry a numeric `price` in US cents (signed manifests); when a service
 * also sends `prices` (`{USD: Money, EUR: Money}`, as money's `GET /v1/items/:id` does) the one in `currency` wins.
 * Without it the USD price is the one we can show honestly.
 */
export function itemPrice(usdMinor: number, currency: Currency = viewerCurrency(), prices?: Prices | null): Money {
  const p = parseMoney(prices?.[currency]);
  return p && p.currency === currency ? p : { amount_minor: usdMinor, currency: "USD" };
}

/** `$4.99`, or "Free" for 0. */
export function priceLabel(usdMinor: number, currency: Currency = viewerCurrency(), prices?: Prices | null): string {
  if (!(usdMinor > 0)) return "Free";
  const m = itemPrice(usdMinor, currency, prices);
  return formatMoney(m.amount_minor, m.currency);
}

/** Money's item path: each segment encoded, the `/` kept (`a_threedom/cap`, `i_game/sword`; routes are `/v1/items/:id*`). */
export function itemPath(itemId: string): string {
  return `/items/${itemId.split("/").map(encodeURIComponent).join("/")}`;
}

/** The lowest price a creator can set: 5 = $0.05. */
export const MIN_PRICE_MINOR = 5;

/**
 * A price typed by a creator (`4.99`, `$4.99`, `5`, `0.5`) → minor units, or an error. Two decimals at most, no
 * thousands separators, at least $0.05 (or €0.05).
 */
export function parsePriceInput(text: string, currency: Currency = "USD"): { minor: number } | { error: string } {
  const t = text.trim().replace(currency === "USD" ? /^\$/ : /^€/, "").trim();
  if (!/^\d{1,6}(\.\d{1,2})?$/.test(t)) return { error: "Type a price like 4.99." };
  const [whole = "0", frac = ""] = t.split(".");
  const minor = Number(whole) * 100 + Number(frac.padEnd(2, "0"));
  if (minor < MIN_PRICE_MINOR) return { error: `The lowest price is ${formatMoney(MIN_PRICE_MINOR, currency)}.` };
  return { minor };
}

/** `499` → `4.99`, for a price field's value. */
export function priceInputValue(minor: number): string {
  return `${Math.floor(minor / 100)}.${String(minor % 100).padStart(2, "0")}`;
}
